// apps/client/src/hooks/reports/useApprovedVsSettledVariance.ts
// Approved-vs-Settled Variance hook — calls getApprovedVsSettledVariance callable.

import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';

export type VarianceFormulaPath = '3-column' | '2-column' | 'mixed' | 'none';
export type VarianceGroupBy = 'template' | 'entity' | 'office' | 'broker';

export interface PerTemplateAvailability {
  templateId: string;
  upfrontRows: number;
  rowsWithVarianceSignal: number;
  pctWithSignal: number;
  formulaPath: VarianceFormulaPath;
  supportsVariance: boolean;
}

export interface VarianceKpis {
  // Variance-subset metrics — only loans matching the active mode filters
  // (has_variance_signal + upfront_actual_cents > 0 + optional decreased-only +
  // optional exclude-construction-likely). Power the leakage cards + tables.
  loanCount: number;
  shortfallLoans: number;
  settledTotalCents: number;
  approvedTotalCents: number;
  upfrontActualCents: number;
  upfrontImpliedFullCents: number;
  upfrontDecreaseCents: number;
  upfrontDecreasePct: number;
  constructionLikelyCount: number;

  // Full-book metrics — every settled loan in the period (respects dimension
  // filters + RBAC, ignores mode toggles). Power the headline "Total Approved /
  // Total Settled / Upfront Paid" cards so they reconcile to the main
  // dashboard's "Loans Settled" figure. See backend kpisFullBookSql.
  fullBookLoanCount: number;
  fullBookSettledCents: number;
  fullBookApprovedCents: number;
  fullBookUpfrontActualCents: number;

  // Subsequent (top-up) upfronts over the full book. Always 0 unless the tenant's
  // approved_vs_settled_subsequent flag is observe/enforce.
  fullBookSubsequentAmountCents: number;
  fullBookSubsequentUpfrontCents: number;
  fullBookSubsequentLoanCount: number;
}

/** Tenant treatment of Loan Market top-up ("subsequent") upfronts; see backend builder. */
export type SubsequentUpfrontMode = 'off' | 'observe' | 'enforce';

export interface VarianceByDimensionRow {
  key: string | null;
  label: string;
  loanCount: number;
  shortfallLoans: number;
  settledTotalCents: number;
  approvedTotalCents: number;
  upfrontActualCents: number;
  upfrontImpliedFullCents: number;
  upfrontDecreaseCents: number;
  constructionLikelyCount: number;
}

export interface VarianceLoanRow {
  loanId: string;
  customerName: string | null;
  settlementDate: string | null;
  lender: string | null;
  brokerDisplay: string | null;
  brokerId: string | null;
  templateId: string | null;
  canonicalOffice: string | null;
  canonicalEntity: string | null;
  settledCents: number;
  approvedCents: number;
  shortfallCents: number;
  shortfallPct: number;
  upfrontActualCents: number;
  upfrontImpliedFullCents: number;
  upfrontDecreaseCents: number;
  /** Top-up (subsequent) loan amount and its upfront; 0 when the tenant flag is off. */
  subsequentAmountCents: number;
  subsequentUpfrontCents: number;
  formulaPath: '3-column' | '2-column';
  isConstructionLikely: boolean;
  isUserExcluded: boolean;
  userAnnotationReason: string | null;
}

export interface ApprovedVsSettledData {
  dataAvailability: {
    perTemplate: PerTemplateAvailability[];
    overallSupportsVariance: boolean;
  };
  kpis: VarianceKpis;
  byDimension: VarianceByDimensionRow[];
  loans: VarianceLoanRow[];
  subsequentUpfrontMode: SubsequentUpfrontMode;
}

export interface UseApprovedVsSettledVarianceParams {
  companyId: string | null | undefined;
  startMonth: string;            // 'YYYY-MM'
  endMonth: string;              // 'YYYY-MM'
  officeId?: string;
  templateId?: string;
  entityCode?: string;
  brokerId?: string;
  groupBy?: VarianceGroupBy;
  decreasedOnly?: boolean;
  excludeConstructionLikely?: boolean;
  loanLimit?: number;
}

export interface UseApprovedVsSettledVarianceReturn {
  data: ApprovedVsSettledData | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook for /reports/approved-vs-settled. Fires a single callable that returns
 * KPIs + per-dimension breakdown + top-N drill-down + per-template availability
 * — page renders all sections from one round-trip.
 */
export function useApprovedVsSettledVariance(
  params: UseApprovedVsSettledVarianceParams,
): UseApprovedVsSettledVarianceReturn {
  const {
    companyId,
    startMonth,
    endMonth,
    officeId,
    templateId,
    entityCode,
    brokerId,
    groupBy = 'entity',
    decreasedOnly = true,
    excludeConstructionLikely = true,
    loanLimit = 200,
  } = params;

  const [data, setData] = useState<ApprovedVsSettledData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refetchTick, setRefetchTick] = useState(0);

  const refetch = useCallback(() => setRefetchTick(t => t + 1), []);

  useEffect(() => {
    if (!companyId || !startMonth || !endMonth) {
      setData(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const callable = httpsCallable<
      Omit<UseApprovedVsSettledVarianceParams, 'companyId'> & { companyId: string },
      ApprovedVsSettledData
    >(functions, 'getApprovedVsSettledVariance');

    callable({
      companyId,
      startMonth,
      endMonth,
      officeId,
      templateId,
      entityCode,
      brokerId,
      groupBy,
      decreasedOnly,
      excludeConstructionLikely,
      loanLimit,
    })
      .then(result => {
        if (cancelled) return;
        setData(result.data);
        setLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('[useApprovedVsSettledVariance] Callable failed:', err);
        setError(err?.message || 'Failed to load variance report');
        setData(null);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    companyId,
    startMonth,
    endMonth,
    officeId,
    templateId,
    entityCode,
    brokerId,
    groupBy,
    decreasedOnly,
    excludeConstructionLikely,
    loanLimit,
    refetchTick,
  ]);

  return { data, loading, error, refetch };
}
